import React, { memo, useEffect, useState } from 'react'
import Link from 'next/link'
import cn from 'classnames'
// Utils
import styles from './BreedList.module.scss'
import useMediaQuery from '@/utils/publicApi'

type BreedLinkProps = {
  name: string
  hasDogs: boolean
  link: string
  count?: number
  selfReferring?: boolean
  type: string
}

const BreedLink = memo(
  ({ name, hasDogs, link, count, selfReferring, type }: BreedLinkProps) => {
    const isMobile = useMediaQuery('(max-width:600px)')
    const shortName = name?.length > 20 ? name?.slice(0, 20) + '...' : name
    return !selfReferring ? (
      <div
        className={cn(styles.listItem, !hasDogs ? styles.grey : styles.link)}
        style={{ cursor: 'pointer' }}
      >
        <Link href={link}>
          <p>
            <span style={{ textTransform: 'capitalize' }}>
              {isMobile ? shortName : name}
            </span>
            {!isMobile && type !== 'byLocation'
              ? count > 1
                ? ` (${count} puppies)`
                : count === 1
                  ? ` (${count} puppy)`
                  : null
              : null}
          </p>
        </Link>
      </div>
    ) : (
      <div className={styles.listItem}>
        <p>
          <span style={{ textTransform: 'capitalize' }}>
            {isMobile ? shortName : name}
          </span>
          {!isMobile
            ? count > 1
              ? ` (${count} puppies)`
              : count === 1
                ? ` (${count} puppy)`
                : null
            : null}
        </p>
      </div>
    )
  }
)

type BreedsListProps = {
  breedsData?: any
  title: string
  less: string
  more: string
  stateName?: string
  stateSlug?: string
  withoutDogs: any[]
  withDogs: any[]
  type: string
  breedSlug?: string
}

const BreedsList = memo(
  ({
    breedsData,
    title,
    less,
    more,
    stateName,
    stateSlug,
    withoutDogs,
    withDogs,
    type,
    breedSlug,
  }: BreedsListProps) => {
    const [showAll, setShowAll] = useState(false)
    const [breeds, setBreeds] = useState([])
    // add hasDog property to breeds with dogs of the state
    withDogs?.forEach((breed) => {
      breed.hasDogs = true
      return breed
    })

    useEffect(() => {
      setBreeds(withDogs)
    }, [breedsData])

    const [hasLoaded, setHasLoaded] = useState(false)
    // Check if Javascript is fully loaded

    useEffect(() => {
      setHasLoaded(true)
    })

    const dataList =
      showAll && hasLoaded ? [...breeds, ...withoutDogs] : [...breeds]
    const noDogsList = showAll
      ? [...withoutDogs]
      : [...withoutDogs].slice(0, 12)

    const linkResolver = (firstRoute, secondRoute) =>
      `/puppies-for-sale/${firstRoute}/${secondRoute}`

    return (
      <div className={styles.breedListWrapper}>
        <div className={styles.article}>
          <div className={styles.articleContent}>
            <h2 className={styles.title}> {title} </h2>
            {type === 'inState' && withDogs.length === 0 && (
              <p className={styles.noDogs}>
                No puppies currently available in {stateName}
              </p>
            )}
            {type === 'byLocation' && withDogs.length === 0 && (
              <p className={styles.noDogs}>
                No {breedSlug} puppies currently available
              </p>
            )}
            <div className={styles.data}>
              {withDogs.length > 0 &&
                dataList
                  .filter(
                    (dog) =>
                      dog.id !== null &&
                      dog.slug !== stateSlug &&
                      dog.slug !== breedSlug
                  )
                  .map(({ name, slug, hasDogs, count, id }) => {
                    let selfReferring = false
                    if (stateSlug && breedSlug)
                      selfReferring = slug === stateSlug || slug === breedSlug
                    return (
                      <BreedLink
                        key={id}
                        name={name}
                        hasDogs={hasDogs}
                        count={count}
                        selfReferring={selfReferring}
                        link={
                          type === 'inState'
                            ? linkResolver(slug, stateSlug)
                            : linkResolver(breedSlug, slug)
                        }
                        type={type}
                      />
                    )
                  })}
              {!hasLoaded &&
                withDogs.length > 0 &&
                withDogs
                  .filter(
                    (dog) =>
                      dog.id !== null &&
                      dog.slug !== stateSlug &&
                      dog.slug !== breedSlug
                  )
                  .map(({ name, slug, hasDogs, count }, i) => {
                    let selfReferring = false
                    if (stateSlug && breedSlug)
                      selfReferring = slug === stateSlug || slug === breedSlug
                    return (
                      <BreedLink
                        key={i}
                        name={name}
                        hasDogs={hasDogs}
                        count={count}
                        selfReferring={selfReferring}
                        link={
                          type === 'inState'
                            ? linkResolver(slug, stateSlug)
                            : linkResolver(breedSlug, slug)
                        }
                        type={type}
                      />
                    )
                  })}
              {showAll &&
                noDogsList.map(({ name, slug, hasDogs }, i) => (
                  <BreedLink
                    key={i}
                    name={name}
                    hasDogs={hasDogs}
                    link={
                      type === 'inState'
                        ? linkResolver(slug, stateSlug)
                        : linkResolver(breedSlug, slug)
                    }
                    type={type}
                  />
                ))}
            </div>
          </div>
          {hasLoaded && (
            <div
              onClick={() => setShowAll((curr) => !curr)}
              className={styles.view}
            >
              <p>
                {showAll ? less : more}{' '}
                <span>
                  <img
                    width="11px"
                    height="6px"
                    alt={showAll ? 'Black up arrow' : 'Black down arrow'}
                    src={
                      showAll
                        ? '/search/arrow-top-black.svg'
                        : '/search/arrow-down-black.svg'
                    }
                  />
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    )
  }
)

export default BreedsList
